/**
 * @author Srijan Shakya
 * @description https://marvelapp.com/4g4b5dg/screen/60580195
 */

import React from 'react';
import loadable from '@loadable/component';
import { connect } from 'react-redux';
import originalMoment from 'moment';
import { extendMoment } from 'moment-range';
import DatePicker from '@components/@core/DatePicker';
import ErrorBoundary from '@components/@core/ErrorBoundary';
import EditDropdown from '@components/EditDropdown';
import Filter from '@components/@core/Filter';
import Icon from '@components/@core/Icon';
import ResponsiveTabs from '@components/@core/ResponsiveTabs';
import Tab from '@components/@core/Tab';
import AppActions from '~/actions/app';
import ManageActions from '~/actions/manage';
import { QuotaReportTabs, ReportMenu } from '~/services/config';
import withNavigate from '~/hocs/withNavigate';
import { exportCsv } from '~/services/util-excel';
import Report from './Report';

const PdfIcon = loadable(() => import('./CompanyQuotaPdf'), { ssr: false });
const moment = extendMoment(originalMoment);

class CompanyQuota extends React.Component {
    state = {
        dateStart: moment().startOf('month').format('YYYY-MM-DD'),
        dateEnd: moment().endOf('month').format('YYYY-MM-DD'),
        companyId: [this.props.companyId],
        userId: [],
        filter: 'all',
        showPrint: false,
    };

    componentDidMount() {
        const { dateStart, dateEnd, companyId, userId } = this.state;
        const payload = {
            date_start: dateStart,
            date_end: dateEnd,
            company_id: companyId,
            user_id: userId,
        };
        this.props.getReports(payload);
    }

    handleFilter = (type, e) => {
        const { dateStart, dateEnd } = this.state;
        const payload = {
            date_start: dateStart,
            date_end: dateEnd,
        };
        if (type === 'company' && e[0].id === -1) {
            // Case for when all comopanies selected
            const companyIds = this.props.companies.map((item) => item.id);
            payload.company_id = companyIds;
            this.props.getReports(payload);
            this.setState({ companyId: [e[0].id] });
        } else {
            // Case when specific company selected or employee selected
            if (type === 'company') {
                const companyIds = e.map((item) => item.id);
                payload.company_id = companyIds;
                this.props.getReports(payload);
                this.setState({ companyId: companyIds });
            } else if (type === 'employee') {
                const userIds = e.map((item) => item.id);
                const companyIds = this.props.companies.map((item) => item.id);
                payload.company_id = companyIds;
                payload.user_id = userIds;
                this.props.getReports(payload);
                this.setState({ userId: userIds });
            }
        }
    };

    handleDate = (e) => {
        const { companyId, userId } = this.state;
        const dateStart = moment(e.start);
        const dateEnd = moment(e.end);

        const payload = {
            date_start: dateStart,
            date_end: dateEnd,
            company_id: companyId,
            user_id: userId,
        };
        this.props.getReports(payload);
        this.setState({ dateStart, dateEnd });
    };

    handleTab = (filter) => {
        const { companyId, userId, dateStart, dateEnd } = this.state;

        const payload = {
            date_start: dateStart,
            date_end: dateEnd,
            company_id: companyId,
            user_id: userId,
        };
        this.props.getReports(payload);
        this.setState({ filter: filter?.id });
    };

    handleQuotaEmployeeModal = (quota) => {
        const payload = { type: 'Quota Employee', data: { before: { quota: quota } } };
        this.props.toggleModal(payload);
    };

    handleIndividualReport = (employee) => {
        const dateStart = moment().subtract(11, 'months').startOf('month').format('YYYY-MM-DD');
        const dateEnd = moment().endOf('month').format('YYYY-MM-DD');
        const payload = {
            userId: employee,
            dateStart,
            dateEnd,
            fliter: 'scorecard',
            page: 1,
            perPage: 10,
        };
        const route = `/hcm/report-quota/${employee}`;
        this.props.getIndividualReport(payload, route);
    };

    handleExcelReport = () => {
        this.props.toggleModal({
            type: 'Confirm',
            data: {
                before: {
                    title: 'Confirm',
                    body: 'Would you like to download this reports data to excel?',
                },
            },
            callBack: {
                onYes: () => {
                    this.handleExcel();
                },
            },
        });
    };

    handleExcel = () => {
        const { filter } = this.state;
        const { quotaReports } = this.props;
        let quotaData = [];

        quotaReports.quota_data.map((quota) => {
            const { name, data, users, actual_count } = quota;
            const { actual_saved_percentage, high, low, average } = data;
            const sheet = {
                Quotas: name,
                'Employees \nAssigned': users?.length,
                'Actuals \nSaved': actual_count,
                '% Actual \nSaved': Number(actual_saved_percentage).toFixed(2) || 'NA',
                Low: Number(low).toFixed(2) || 'NA',
                High: Number(high).toFixed(2) || 'NA',
                Average: Number(average).toFixed(2) || 'NA',
            };
            quotaData.push(sheet);
        });
        const key = ['Quotas', 'Employees \nAssigned', 'Actuals \nSaved', '% Actual \nSaved', 'Low', 'High', 'Average'];
        exportCsv(quotaData, key, `Report-Quota-${filter}`, true);
    };

    handleReportMenu = (e) => {
        switch (e) {
            case 'export':
                this.handleExcelReport();
                break;
            case 'print':
                this.setState({ showPrint: true });
                break;
        }
    };

    render() {
        const { filter, dateStart, dateEnd } = this.state;
        const date = moment.range(dateStart, dateEnd);

        return (
            <ErrorBoundary className="quota-reports">
                <Filter onChange={this.handleFilter} />
                <div className="card">
                    <div className="d-h-between mb-2 mt-4 ai-center">
                        <span className="dsl-b22 bold">Employees Quotas</span>
                        <div className="d-flex">
                            <DatePicker calendar="month" format="MMM YY" as="span" align="right" append="caret" value={date} onSelect={this.handleDate} />
                            <EditDropdown className="ml-3" dataCy="editDropdown" options={ReportMenu} onChange={this.handleReportMenu} />
                            {this.state.showPrint && <PdfIcon state={this.state} props={this.props} name="Print" />}
                        </div>
                    </div>
                    <ResponsiveTabs data={QuotaReportTabs} active={filter} getId={(e) => e['name']} getValue={(e) => e['label']} onTab={this.handleTab} />
                    {QuotaReportTabs.map(
                        (tab) =>
                            tab.name === filter && (
                                <Tab eventKey={filter} active={filter}>
                                    <Report
                                        tab={tab.name}
                                        data={this.props.quotaReports}
                                        navigate={this.props.navigate}
                                        location={this.props.location}
                                        onReport={this.handleIndividualReport}
                                        onShowModal={this.handleQuotaEmployeeModal}
                                    />
                                </Tab>
                            )
                    )}
                </div>
            </ErrorBoundary>
        );
    }
}

const mapStateToProps = (state) => ({
    companies: state.app.companies,
    companyId: state.app.company_info.id,
    quotaReports: state.manage.quotaReports,
});

const mapDispatchToProps = (dispatch) => ({
    getReports: (payload) => dispatch(ManageActions.postquotareportRequest(payload)),
    getIndividualReport: (payload, route) => dispatch(ManageActions.postindividualquotareportRequest(payload, route)),
    toggleModal: (e) => dispatch(AppActions.modalRequest(e)),
});

const CompanyQuotaWithNavigate = withNavigate(CompanyQuota);

export default connect(mapStateToProps, mapDispatchToProps)(CompanyQuotaWithNavigate);
